import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { AuthProvider } from '@/auth/AuthProvider'
import { ConfigProvider } from '@/config/ConfigProvider'
import { ToastProvider } from '@/components/ui/Toast'
import { AppRoutes } from '@/routes'

/** Defaults per docs/04-frontend-architecture.md §4. */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // Retrying a 4xx just delays the error, and so does a 501 (an endpoint
      // the Portal API does not serve yet). Other 5xx get one retry.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status
        if (typeof status === 'number' && (status < 500 || status === 501)) return false
        return failureCount < 1
      },
    },
  },
})

/**
 * Provider order is load-bearing.
 *
 * ConfigProvider is outermost below the query client because the Auth0 client
 * cannot be constructed without a domain and client id, and it renders nothing
 * until `/config.json` resolves.
 *
 * AuthProvider sits INSIDE BrowserRouter, not outside it: the Auth0 redirect
 * callback restores the path the user was heading for, and doing that through
 * `useNavigate` rather than `history.replaceState` is the only version React
 * Router actually observes.
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider>
        <ToastProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </ConfigProvider>
    </QueryClientProvider>
  )
}
